import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { FiX, FiUpload, FiFile, FiImage, FiExternalLink, FiDownload, FiTrash2 } from 'react-icons/fi';
import { API_URL, API_BASE_URL, MAX_UPLOAD_BYTES } from '../config';
import { getAuthToken } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const MAX_BYTES = MAX_UPLOAD_BYTES;
const ACCEPT = '.pdf,.png,.jpg,.jpeg,.gif,.webp,.heic,.tif,.tiff,.dwg,.dxf,.dgn,.kml,.kmz,.gpx,.doc,.docx,.xls,.xlsx,.csv,.txt,.rtf,.zip';

const fmtBytes = (b) => b == null ? '' : b < 1024 ? `${b} B` : b < 1048576 ? `${(b / 1024).toFixed(1)} KB` : `${(b / 1048576).toFixed(1)} MB`;
const isImage = (m, n) => (m && m.startsWith('image/')) || ['png', 'jpg', 'jpeg', 'gif', 'webp', 'heic', 'tif', 'tiff'].includes((n || '').split('.').pop()?.toLowerCase());
const isPdf = (m, n) => m === 'application/pdf' || (n || '').toLowerCase().endsWith('.pdf');

// Crew-facing file attachments for a single work order (e.g. a PDF drawing to pull up in the field).
// These are internal only — never shown to clients.
export default function WorkOrderAttachmentsModal({ jobId, workOrder, onClose }) {
  const { user } = useAuth();
  const isAdmin = user?.can_manage_payroll;
  const [items, setItems] = useState([]);
  const [pending, setPending] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const authCfg = useCallback(async () => {
    const token = await getAuthToken();
    return { headers: token ? { Authorization: `Bearer ${token}` } : {} };
  }, []);

  const load = useCallback(async () => {
    try {
      const cfg = await authCfg();
      const res = await axios.get(`${API_URL}/jobs/${jobId}/attachments?work_order_id=${workOrder.id}`, cfg);
      setItems(res.data?.attachments || []);
    } catch (e) { /* ignore */ }
  }, [jobId, workOrder.id, authCfg]);

  useEffect(() => { load(); }, [load]);

  const pick = () => fileRef.current?.click();
  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_BYTES) { alert(`File too large (${fmtBytes(file.size)}). Max ${fmtBytes(MAX_BYTES)}.`); e.target.value = ''; return; }
    setPending(file);
  };

  const upload = async () => {
    if (!pending) return;
    setUploading(true);
    try {
      const token = await getAuthToken();
      const fd = new FormData();
      fd.append('file', pending);
      fd.append('work_order_id', String(workOrder.id));
      await axios.post(`${API_URL}/jobs/${jobId}/attachments`, fd, { headers: { Authorization: token ? `Bearer ${token}` : undefined } });
      setPending(null);
      if (fileRef.current) fileRef.current.value = '';
      await load();
    } catch (err) {
      alert('Upload failed: ' + (err.response?.data?.error || err.message));
    }
    setUploading(false);
  };

  const openView = async (att) => {
    const token = await getAuthToken();
    window.open(`${API_BASE_URL || ''}${att.view_url}?token=${encodeURIComponent(token)}`, '_blank');
  };
  const download = async (att) => {
    const token = await getAuthToken();
    window.open(`${API_BASE_URL || ''}${att.download_url}?token=${encodeURIComponent(token)}`, '_blank');
  };
  const del = async (att) => {
    if (!isAdmin) { alert('Only admins can delete attachments.'); return; }
    if (!window.confirm(`Delete "${att.original_filename}"?`)) return;
    try { const cfg = await authCfg(); await axios.delete(`${API_URL}/jobs/${jobId}/attachments/${att.id}`, cfg); await load(); }
    catch (e) { alert('Delete failed: ' + (e.response?.data?.error || e.message)); }
  };

  const iconFor = (a) => isImage(a.mime_type, a.original_filename)
    ? <FiImage style={{ color: '#16a34a' }} />
    : isPdf(a.mime_type, a.original_filename) ? <FiFile style={{ color: '#dc2626' }} /> : <FiFile style={{ color: '#6b7280' }} />;

  return (
    <div className="qv-modal-overlay" onClick={onClose}>
      <div className="qv-modal" style={{ maxWidth: 560, width: '95%' }} onClick={(e) => e.stopPropagation()}>
        <div className="qv-modal-head">
          <h3>{workOrder.order_number} — Attachments</h3>
          <button className="btn-ghost" onClick={onClose}><FiX /> Close</button>
        </div>
        <div className="qv-modal-body">
          <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 12 }}>
            Crew-only files for this work order (e.g. a PDF drawing). Not shown to clients.
          </div>
          <div style={{ marginBottom: 12 }}>
            <button className="btn btn-primary btn-sm" onClick={pick} disabled={uploading}><FiUpload /> Add File</button>
            <input ref={fileRef} type="file" accept={ACCEPT} onChange={onFile} style={{ display: 'none' }} />
          </div>
          {pending && (
            <div style={{ padding: 12, marginBottom: 12, background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8 }}>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>{pending.name} <span style={{ color: '#6b7280', fontWeight: 400 }}>({fmtBytes(pending.size)})</span></div>
              <button className="btn btn-primary btn-sm" onClick={upload} disabled={uploading} style={{ marginRight: 8 }}>{uploading ? 'Uploading…' : 'Upload'}</button>
              <button className="btn btn-sm" onClick={() => { setPending(null); if (fileRef.current) fileRef.current.value = ''; }} disabled={uploading}>Cancel</button>
            </div>
          )}
          {items.length === 0 ? (
            <div style={{ padding: '16px 0', color: '#9ca3af', fontSize: 13, textAlign: 'center' }}>No files on this work order yet.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {items.map(a => (
                <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', border: '1px solid #e5e7eb', borderRadius: 8 }}>
                  <div style={{ flexShrink: 0 }}>{iconFor(a)}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div onClick={() => openView(a)} title={a.original_filename}
                      style={{ fontSize: 14, fontWeight: 500, color: '#2563eb', cursor: 'pointer', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {a.original_filename || 'Untitled'}
                    </div>
                    <div style={{ fontSize: 12, color: '#6b7280' }}>{fmtBytes(a.file_size)}{a.uploaded_by_name ? ` · ${a.uploaded_by_name}` : ''}</div>
                  </div>
                  <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                    {(isImage(a.mime_type, a.original_filename) || isPdf(a.mime_type, a.original_filename)) && (
                      <button className="btn btn-sm" onClick={() => openView(a)} title="View"><FiExternalLink size={14} /></button>
                    )}
                    <button className="btn btn-sm" onClick={() => download(a)} title="Download"><FiDownload size={14} /></button>
                    {isAdmin && <button className="btn btn-sm" style={{ color: '#dc2626' }} onClick={() => del(a)} title="Delete"><FiTrash2 size={14} /></button>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="qv-modal-foot">
          <button className="btn btn-secondary" onClick={onClose}>Done</button>
        </div>
      </div>
    </div>
  );
}
